import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { addDays, addWeeks, format, isSameDay, startOfDay, startOfWeek } from 'date-fns'
import { nb } from 'date-fns/locale'
import { CalendarDays, ChevronLeft, ChevronRight, Info, Plus, Repeat } from 'lucide-react'
import { apiDelete, apiGet, apiPost } from '../api'
import { useFacility } from '../context/FacilityContext'
import { Alert, Button, EmptyState, IconButton, Modal, PageHeader, Tabs } from '../components/ui'
import SlotForm from './rentals/SlotForm'
import SlotDetail from './rentals/SlotDetail'
import AgreementForm from './rentals/AgreementForm'
import AgreementsTab from './rentals/AgreementsTab'
import CourtsTab from './rentals/CourtsTab'
import {
  COURT_PALETTE,
  DAY_END_HOUR,
  DAY_START_HOUR,
  HOUR_HEIGHT,
  NO_COURT_STYLE,
  SNAP_MINUTES,
  Swatch,
  fmtPrice,
  fmtTime,
  formToPayload,
  layoutEvents,
  pad,
  readError,
  toDateInput,
  toLocalInput
} from './rentals/shared'
import './rentals.css'

const TAB_KEYS = ['calendar', 'agreements', 'courts']

function RentalsPage() {
  const { activeFacilityId, activeFacility } = useFacility()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const tab = TAB_KEYS.includes(tabParam) ? tabParam : 'calendar'
  const setTab = useCallback(
    (key) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (key === 'calendar') next.delete('tab')
          else next.set('tab', key)
          return next
        },
        { replace: true }
      ),
    [setSearchParams]
  )
  const [view, setView] = useState('week')
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()))
  const [entries, setEntries] = useState([])
  const [courts, setCourts] = useState([])
  const [agreements, setAgreements] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [createDraft, setCreateDraft] = useState(null)
  const [selected, setSelected] = useState(null)
  const [showAgreement, setShowAgreement] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [courtName, setCourtName] = useState('')
  const [drag, setDrag] = useState(null)
  const gridRef = useRef(null)

  const range = useMemo(() => {
    if (view === 'day') return { from: anchor, to: addDays(anchor, 1) }
    const from = startOfWeek(anchor, { weekStartsOn: 1 })
    return { from, to: addDays(from, 7) }
  }, [anchor, view])

  const days = useMemo(() => {
    const n = view === 'day' ? 1 : 7
    return Array.from({ length: n }, (_, i) => addDays(range.from, i))
  }, [range, view])

  // Fargesett (fyll, tekst, kant) per hall
  const courtStyle = useCallback(
    (courtId) => {
      if (!courtId) return NO_COURT_STYLE
      const idx = courts.findIndex((c) => c.id === courtId)
      return COURT_PALETTE[(idx >= 0 ? idx : 0) % COURT_PALETTE.length]
    },
    [courts]
  )

  const loadCourts = useCallback(async () => {
    if (!activeFacilityId) return
    const res = await apiGet(`/api/facilities/${activeFacilityId}/rental-courts`)
    if (res.ok) setCourts(await res.json())
  }, [activeFacilityId])

  const loadSlots = useCallback(async () => {
    if (!activeFacilityId) return
    setLoading(true)
    setError('')
    try {
      const qs = `from=${range.from.toISOString()}&to=${range.to.toISOString()}`
      const res = await apiGet(`/api/facilities/${activeFacilityId}/rentals/admin?${qs}`)
      if (!res.ok) throw new Error(await readError(res, 'Kunne ikke hente utleier'))
      setEntries(await res.json())
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [activeFacilityId, range])

  const loadAgreements = useCallback(async () => {
    if (!activeFacilityId) return
    const res = await apiGet(`/api/facilities/${activeFacilityId}/rental-agreements`)
    if (res.ok) setAgreements(await res.json())
  }, [activeFacilityId])

  useEffect(() => { loadCourts() }, [loadCourts])
  useEffect(() => { loadSlots() }, [loadSlots])
  useEffect(() => { if (tab === 'agreements') loadAgreements() }, [tab, loadAgreements])

  // Scroll til ca. kl. 08 første gang
  useEffect(() => {
    if (gridRef.current) gridRef.current.scrollTop = (8 - DAY_START_HOUR) * HOUR_HEIGHT
  }, [tab])

  const step = (dir) => setAnchor((a) => (view === 'day' ? addDays(a, dir) : addWeeks(a, dir)))

  // Kolonner: uke = dager, dag = haller (+ «uten hall»)
  const columns = useMemo(() => {
    if (view === 'week') return days.map((d) => ({ key: d.toISOString(), day: d, courtId: undefined, label: format(d, 'EEE d.', { locale: nb }) }))
    const cols = courts.map((c) => ({ key: c.id, day: days[0], courtId: c.id, label: c.name }))
    cols.push({ key: 'none', day: days[0], courtId: null, label: courts.length ? 'Uten hall' : 'Utleie' })
    return cols
  }, [view, days, courts])

  const eventsFor = (col) => {
    const dayStart = startOfDay(col.day).getTime()
    const dayEnd = dayStart + 24 * 3600 * 1000
    const items = entries
      .filter((e) => {
        const s = new Date(e.slot.startsAt).getTime()
        const en = new Date(e.slot.endsAt).getTime()
        if (!(s < dayEnd && en > dayStart)) return false
        if (col.courtId === undefined) return true
        return (e.slot.courtId || null) === col.courtId
      })
      .map((e) => ({
        entry: e,
        start: Math.max(new Date(e.slot.startsAt).getTime(), dayStart),
        end: Math.min(new Date(e.slot.endsAt).getTime(), dayEnd)
      }))
    return layoutEvents(items).map((ev) => {
      const startMin = (ev.start - dayStart) / 60000 - DAY_START_HOUR * 60
      const endMin = (ev.end - dayStart) / 60000 - DAY_START_HOUR * 60
      return {
        ...ev,
        top: Math.max(0, (startMin / 60) * HOUR_HEIGHT),
        height: Math.max(22, ((endMin - Math.max(0, startMin)) / 60) * HOUR_HEIGHT - 2)
      }
    })
  }

  const minutesFromY = (y) => {
    const raw = (y / HOUR_HEIGHT) * 60
    return Math.max(0, Math.min((DAY_END_HOUR - DAY_START_HOUR) * 60, Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES))
  }

  const onColumnMouseDown = (col) => (e) => {
    if (e.button !== 0 || e.target.closest('.rt-event')) return
    const rect = e.currentTarget.getBoundingClientRect()
    const m = minutesFromY(e.clientY - rect.top)
    setDrag({ col, rect, startMin: m, endMin: m + 60 })
  }

  useEffect(() => {
    if (!drag) return
    const onMove = (e) => {
      const m = minutesFromY(e.clientY - drag.rect.top)
      setDrag((d) => (d ? { ...d, endMin: Math.max(m, d.startMin + SNAP_MINUTES) } : d))
    }
    const onUp = () => {
      setDrag((d) => {
        if (d) {
          const base = startOfDay(d.col.day)
          const start = new Date(base.getTime() + (DAY_START_HOUR * 60 + d.startMin) * 60000)
          const end = new Date(base.getTime() + (DAY_START_HOUR * 60 + d.endMin) * 60000)
          openCreate(start, end, d.col.courtId || '')
        }
        return null
      })
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag])

  const openCreate = (start, end, courtId = '') => {
    setCreateDraft({
      title: '',
      courtId,
      visibility: 'FOLLOWERS',
      startsAt: toLocalInput(start),
      endsAt: toLocalInput(end),
      capacity: 1,
      price: 0,
      cancellationDeadlineHours: 24,
      locationText: '',
      description: ''
    })
  }

  const openCreateDefault = () => {
    const now = new Date()
    const base = view === 'day' ? new Date(anchor) : now
    base.setHours(now.getHours() + 1, 0, 0, 0)
    openCreate(base, new Date(base.getTime() + 3600 * 1000))
  }

  const createSlot = async (form) => {
    setSubmitting(true)
    setError('')
    try {
      const res = await apiPost(`/api/facilities/${activeFacilityId}/rentals`, formToPayload(form))
      if (!res.ok) throw new Error(await readError(res, 'Kunne ikke opprette utleien'))
      setCreateDraft(null)
      await loadSlots()
    } catch (e) {
      setError(e.message)
    } finally {
      setSubmitting(false)
    }
  }

  const createAgreement = async (form) => {
    setSubmitting(true)
    setError('')
    try {
      const res = await apiPost(`/api/facilities/${activeFacilityId}/rental-agreements`, {
        phone: form.phone.trim() || null,
        name: form.name.trim() || null,
        title: form.title.trim(),
        courtId: form.courtId || null,
        weekdays: form.weekdays,
        startTime: form.startTime,
        endTime: form.endTime,
        startDate: form.startDate,
        endDate: form.endDate,
        locationText: form.locationText.trim() || null,
        description: form.description.trim() || null
      })
      if (!res.ok) throw new Error(await readError(res, 'Kunne ikke opprette avtalen'))
      setShowAgreement(false)
      await Promise.all([loadSlots(), loadAgreements()])
    } catch (e) {
      setError(e.message)
    } finally {
      setSubmitting(false)
    }
  }

  // Bekreftes i tabellraden: «Avslutte avtalen «…» med …? Fremtidige ganger avlyses.»
  const endAgreement = async (a) => {
    const res = await apiDelete(`/api/facilities/${activeFacilityId}/rental-agreements/${a.id}`)
    if (!res.ok) {
      setError(await readError(res, 'Kunne ikke avslutte avtalen'))
      return
    }
    await Promise.all([loadAgreements(), loadSlots()])
  }

  const addCourt = async (e) => {
    e.preventDefault()
    if (!courtName.trim()) return
    const res = await apiPost(`/api/facilities/${activeFacilityId}/rental-courts`, { name: courtName.trim() })
    if (!res.ok) {
      setError(await readError(res, 'Kunne ikke legge til hallen'))
      return
    }
    setCourtName('')
    loadCourts()
  }

  // Bekreftes i listen: «Fjerne «…»? Eksisterende utleier beholdes.»
  const removeCourt = async (c) => {
    const res = await apiDelete(`/api/facilities/${activeFacilityId}/rental-courts/${c.id}`)
    if (res.ok) loadCourts()
  }

  const onSlotChanged = (updated) => {
    if (updated) setSelected(updated)
    loadSlots()
  }

  // Stabile lukkefunksjoner (Modal fokuserer første felt på nytt hvis onClose endres)
  const closeCreate = useCallback(() => setCreateDraft(null), [])
  const closeDetail = useCallback(() => setSelected(null), [])
  const closeAgreement = useCallback(() => setShowAgreement(false), [])
  const openAgreement = () => setShowAgreement(true)

  const rangeLabel =
    view === 'day'
      ? format(anchor, 'EEEE d. MMMM yyyy', { locale: nb })
      : `${format(range.from, 'd. MMM', { locale: nb })} – ${format(addDays(range.to, -1), 'd. MMM yyyy', { locale: nb })}`

  const hours = Array.from({ length: DAY_END_HOUR - DAY_START_HOUR }, (_, i) => DAY_START_HOUR + i)
  const now = new Date()
  const unit = view === 'day' ? 'dag' : 'uke'
  const gridCols = `56px repeat(${columns.length}, minmax(120px, 1fr))`

  if (!activeFacilityId) {
    return (
      <div className="page">
        <PageHeader title="Utleie" />
        <EmptyState icon={CalendarDays} title="Velg et anlegg for å administrere utleie." />
      </div>
    )
  }

  return (
    <div className="page rt-page">
      <PageHeader
        title="Utleie"
        subtitle={`Legg ut ledige tider i hallene${activeFacility ? ` hos ${activeFacility.name}` : ''}. Følgerne booker i appen.`}
      />

      <Tabs
        label="Utleie"
        active={tab}
        onChange={setTab}
        tabs={[
          { key: 'calendar', label: 'Kalender' },
          { key: 'agreements', label: 'Faste avtaler' },
          { key: 'courts', label: `Haller (${courts.length})` }
        ]}
      />

      {error && <Alert tone="error">{error}</Alert>}

      {tab === 'calendar' && (
        <div className="rt-calendar">
          <div className="rt-toolbar">
            <div className="rt-toolbar-nav">
              <Button variant="tertiary" size="sm" className="rt-tb-btn" onClick={() => setAnchor(startOfDay(new Date()))}>I dag</Button>
              <IconButton icon={ChevronLeft} label={`Forrige ${unit}`} onClick={() => step(-1)} />
              <IconButton icon={ChevronRight} label={`Neste ${unit}`} onClick={() => step(1)} />
              <label className="rt-date">
                <span className="sr-only">Gå til dato</span>
                <input
                  type="date"
                  className="input input-mono rt-date-input"
                  value={toDateInput(anchor)}
                  onChange={(e) => e.target.value && setAnchor(startOfDay(new Date(`${e.target.value}T00:00`)))}
                />
              </label>
            </div>
            <div className="rt-toolbar-label">
              <span className="rt-range-label" aria-live="polite">{rangeLabel}</span>
              {loading && <span className="status-line">Laster…</span>}
            </div>
            <div className="segmented" role="group" aria-label="Visning">
              <button type="button" aria-pressed={view === 'week'} onClick={() => setView('week')}>Uke</button>
              <button type="button" aria-pressed={view === 'day'} onClick={() => setView('day')}>Dag</button>
            </div>
            <div className="rt-toolbar-actions">
              <Button variant="secondary" icon={Repeat} iconSize={17} onClick={openAgreement}>Ny avtale</Button>
              <Button variant="primary" icon={Plus} iconSize={17} onClick={openCreateDefault}>Ny utleie</Button>
            </div>
          </div>

          {courts.length > 0 && (
            <ul className="rt-legend" aria-label="Haller">
              {courts.map((c) => (
                <li key={c.id}><Swatch style={courtStyle(c.id)} />{c.name}</li>
              ))}
              <li><Swatch style={NO_COURT_STYLE} />Ingen bestemt hall</li>
            </ul>
          )}

          <div className="rt-grid-card">
            <div className="rt-grid-scroll" ref={gridRef}>
              <div className="rt-grid-head" style={{ gridTemplateColumns: gridCols }}>
                <span className="rt-corner mono">{pad(DAY_START_HOUR)}–{DAY_END_HOUR}</span>
                {columns.map((col) => {
                  const today = view === 'week' && isSameDay(col.day, now)
                  return (
                    <div key={col.key} className={`rt-col-head ${today ? 'is-today' : ''}`}>
                      {view === 'week' ? (
                        <button
                          type="button"
                          className="rt-day-link"
                          onClick={() => { setAnchor(startOfDay(col.day)); setView('day') }}
                          aria-label={`Vis ${format(col.day, 'EEEE d. MMMM', { locale: nb })} i dagvisning`}
                          aria-current={today ? 'date' : undefined}
                        >
                          <span className="rt-wd">{format(col.day, 'EEE', { locale: nb })}</span>
                          <span className="rt-dn">{format(col.day, 'd.')}</span>
                        </button>
                      ) : (
                        <span className="rt-court-head"><Swatch style={courtStyle(col.courtId)} size={12} />{col.label}</span>
                      )}
                    </div>
                  )
                })}
              </div>

              <div className="rt-grid" style={{ gridTemplateColumns: gridCols }}>
                <div className="rt-hours" aria-hidden="true">
                  {hours.map((h) => <div key={h} className="rt-hour" style={{ height: HOUR_HEIGHT }}>{pad(h)}:00</div>)}
                </div>
                {columns.map((col) => {
                  const today = isSameDay(col.day, now)
                  return (
                    <div
                      key={col.key}
                      className={`rt-col ${view === 'week' && today ? 'is-today' : ''}`}
                      style={{ height: hours.length * HOUR_HEIGHT }}
                      onMouseDown={onColumnMouseDown(col)}
                    >
                      {hours.map((h) => <div key={h} className="rt-slot-line" style={{ top: (h - DAY_START_HOUR) * HOUR_HEIGHT }} />)}
                      {today && now.getHours() >= DAY_START_HOUR && (
                        <div
                          className="rt-now"
                          role="img"
                          aria-label={`Nå ${fmtTime(now)}`}
                          style={{ top: ((now.getHours() - DAY_START_HOUR) * 60 + now.getMinutes()) / 60 * HOUR_HEIGHT }}
                        />
                      )}
                      {eventsFor(col).map((ev) => {
                        const s = ev.entry.slot
                        const full = s.spotsLeft === 0
                        const cs = courtStyle(s.courtId)
                        const time = `${fmtTime(new Date(s.startsAt))}–${fmtTime(new Date(s.endsAt))}`
                        return (
                          <button
                            type="button"
                            key={s.id}
                            className={`rt-event ${full ? 'is-full' : ''} ${ev.height < 44 ? 'is-short' : ''} ${ev.laneCount > 1 && view === 'week' ? 'is-narrow' : ''}`}
                            style={{
                              top: ev.top,
                              height: ev.height,
                              left: `calc(${(ev.lane / ev.laneCount) * 100}% + 3px)`,
                              width: `calc(${100 / ev.laneCount}% - 6px)`,
                              background: cs.bg,
                              color: cs.fg,
                              borderColor: cs.bd,
                              '--rt-sub': cs.sub
                            }}
                            onClick={() => setSelected(ev.entry)}
                            title={`${s.title} ${time}`}
                          >
                            <span className="rt-event-top">
                              <span className="rt-event-time">
                                <span className="rt-t-full">{time}</span>
                                <span className="rt-t-start">{fmtTime(new Date(s.startsAt))}</span>
                              </span>
                              {full && <span className="rt-full-pill">Fullt</span>}
                            </span>
                            <span className="rt-event-title">
                              {s.agreementId && <Repeat size={12} strokeWidth={2.4} aria-label="Fast avtale" />}
                              <span>{s.title}</span>
                            </span>
                            <span className="rt-event-meta">
                              {view === 'week' && s.courtName ? `${s.courtName} · ` : ''}
                              {s.bookedCount}/{s.capacity}{s.priceOre > 0 ? ` · ${fmtPrice(s.priceOre)}` : ''}
                              {s.visibility === 'PUBLIC' ? ' · Offentlig' : ''}
                            </span>
                          </button>
                        )
                      })}
                      {drag && drag.col.key === col.key && (
                        <div
                          className="rt-drag"
                          style={{ top: (drag.startMin / 60) * HOUR_HEIGHT, height: ((drag.endMin - drag.startMin) / 60) * HOUR_HEIGHT }}
                        >
                          {pad(DAY_START_HOUR + Math.floor(drag.startMin / 60))}:{pad(drag.startMin % 60)}–{pad(DAY_START_HOUR + Math.floor(drag.endMin / 60))}:{pad(drag.endMin % 60)}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          <p className="rt-hint">
            <Info size={18} strokeWidth={1.9} aria-hidden="true" />
            <span>Tips: dra i kalenderen for å lage en ny utleie. Klikk på en utleie for å redigere eller se hvem som har booket.</span>
          </p>
        </div>
      )}

      {tab === 'agreements' && (
        <AgreementsTab agreements={agreements} onNew={openAgreement} onEnd={endAgreement} courtStyle={courtStyle} />
      )}

      {tab === 'courts' && (
        <CourtsTab
          courts={courts}
          courtName={courtName}
          setCourtName={setCourtName}
          onAdd={addCourt}
          onRemove={removeCourt}
          courtStyle={courtStyle}
        />
      )}

      {createDraft && (
        <Modal title="Ny utleie" subtitle="Tiden blir synlig for følgerne og kan bookes i appen." onClose={closeCreate} size="rt-form-modal">
          <SlotForm
            initial={createDraft}
            courts={courts}
            onSubmit={createSlot}
            submitting={submitting}
            submitLabel="Opprett utleie"
            onCancel={closeCreate}
            error={error}
            isNew
          />
        </Modal>
      )}

      {selected && (
        <SlotDetail
          entry={selected}
          courts={courts}
          facilityId={activeFacilityId}
          onChanged={onSlotChanged}
          onClose={closeDetail}
          courtStyle={courtStyle}
        />
      )}

      {showAgreement && (
        <Modal title="Ny fast avtale" onClose={closeAgreement}>
          <AgreementForm courts={courts} onSubmit={createAgreement} submitting={submitting} onCancel={closeAgreement} error={error} />
        </Modal>
      )}
    </div>
  )
}

export default RentalsPage
